/**
 * Quick Add Forms Component
 * 
 * Syncs quick-add form selects with ancestor product-card's data-option-value attribute.
 * Updates select[name="id"] to match the variant with the corresponding data-option-X value.
 * 
 * Features:
 * - Finds ancestor product-card element
 * - Reads data-option-value from product-card
 * - Matches against data-option-0, data-option-1, data-option-2... on select options
 * - Updates on load if visible, or when becomes visible via Intersection Observer
 * - Listens for non-bubbling custom event on product-card when data-option-value changes
 */

(function() {
  'use strict';

  const initializedForms = new WeakSet();
  let intersectionObserver = null;

  /**
   * Find the ancestor product-card element
   * @param {HTMLElement} element
   * @returns {HTMLElement|null}
   */
  function findAncestorProductCard(element) {
    let current = element.parentElement;
    while (current) {
      if (current.tagName && current.tagName.toLowerCase() === 'product-card') {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  /**
   * Find matching option in select based on product-card's data-option-value
   * Loops through data-option-0, data-option-1, data-option-2... until a match is found
   * @param {HTMLSelectElement} select
   * @param {string} optionValue
   * @returns {HTMLOptionElement|null}
   */
  function findMatchingOption(select, optionValue) {
    if (!select || !optionValue) {
      return null;
    }

    const options = select.querySelectorAll('option');
    
    // Loop through option indices (0, 1, 2...) until we find a match
    for (let index = 0; index < 10; index++) { // Max 10 options should be enough
      const dataAttributeName = `data-option-${index}`;
      
      for (const option of options) {
        const optionDataValue = option.getAttribute(dataAttributeName);
        if (optionDataValue === optionValue) {
          return option;
        }
      }
    }
    
    return null;
  }

  /**
   * Update a quick-add form's select to match product-card's data-option-value
   * @param {HTMLElement} formContainer
   */
  function updateQuickAddForm(formContainer) {
    if (!formContainer) {
      return;
    }

    // Find ancestor product-card
    const productCard = findAncestorProductCard(formContainer);
    if (!productCard) {
      return;
    }

    // Get data-option-value from product-card
    const optionValue = productCard.getAttribute('data-option-value');
    if (!optionValue) {
      return;
    }

    // Find all select[name="id"] elements within this form container
    const selects = formContainer.querySelectorAll('select[name="id"]');
    
    selects.forEach(select => {
      const matchingOption = findMatchingOption(select, optionValue);
      
      if (matchingOption && select.value !== matchingOption.value) {
        select.value = matchingOption.value;
        // Trigger change event in case other code is listening
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /**
   * Initialize a quick-add form container
   * @param {HTMLElement} formContainer
   */
  function initializeForm(formContainer) {
    if (!formContainer || initializedForms.has(formContainer)) {
      return;
    }

    initializedForms.add(formContainer);

    // Check if element is visible
    const rect = formContainer.getBoundingClientRect();
    const isVisible = rect.width > 0 && rect.height > 0 && 
                     window.getComputedStyle(formContainer).visibility !== 'hidden';

    if (isVisible) {
      // Update immediately if visible
      updateQuickAddForm(formContainer);
    } else {
      // Use Intersection Observer if not visible
      ensureIntersectionObserver();
      if (intersectionObserver) {
        intersectionObserver.observe(formContainer);
      }
    }

    // Listen for non-bubbling custom event on product-card
    // Since the event is non-bubbling, we must attach the listener directly to the product-card
    const productCard = findAncestorProductCard(formContainer);
    if (productCard) {
      // Store reference to formContainer in closure for the event handler
      const handleOptionChange = function(event) {
        updateQuickAddForm(formContainer);
      };
      
      productCard.addEventListener('product-card:option-changed', handleOptionChange);
      
      // Store handler reference for potential cleanup (though not strictly necessary here)
      if (!productCard._quickAddFormHandlers) {
        productCard._quickAddFormHandlers = new WeakMap();
      }
      productCard._quickAddFormHandlers.set(formContainer, handleOptionChange);
    }
  }

  /**
   * Create Intersection Observer for lazy initialization
   */
  function ensureIntersectionObserver() {
    if (intersectionObserver || typeof IntersectionObserver === 'undefined') {
      return intersectionObserver;
    }

    intersectionObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          updateQuickAddForm(entry.target);
          // Unobserve after first intersection
          intersectionObserver.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '50px',
      threshold: 0.01
    });

    return intersectionObserver;
  }

  /**
   * Initialize all quick-add forms
   */
  function initializeAllForms() {
    const formContainers = document.querySelectorAll('[data-quick-add-forms="true"]');
    formContainers.forEach(container => {
      initializeForm(container);
    });
  }


  /**
   * Initialize component
   */
  function init() {
    // Initialize existing forms
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initializeAllForms();
      });
    } else {
      initializeAllForms();
    }

    // Watch for newly added forms
    const formObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node is a form container
            if (node.hasAttribute && node.hasAttribute('data-quick-add-forms')) {
              initializeForm(node);
            }
            // Check for form containers within the added node
            const formContainers = node.querySelectorAll && node.querySelectorAll('[data-quick-add-forms="true"]');
            if (formContainers) {
              formContainers.forEach(container => {
                initializeForm(container);
              });
            }
          }
        });
      });
    });

    formObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize when script loads
  init();
})();

